
import {
    FrontendFilterPreset,
    FrontendFilter,
    FrontendFilterConfig,
    FrontendFilterGroup, FrontendFilterInputDate,
    FrontendFilterOption,
    FrontendFilterResultColumn,
    FrontendFilterValues,
    FrontendFilterResultRowModifierCssClasses, FrontendFilterResultRowPreprocessor, FrontendFilterBatchAction, FrontendFilterResultRowModifier
} from "@/components/FrontendFilters/structs";
import OrderSearchFilter from "@/views/OrderLists/components/OrderSearchFilter.vue";
import FrontendFilterInput from "@/components/FrontendFilters/FrontendFilterInput.vue";
import GenericHelpers from "@/helpers/generic-helpers"
import _, {cloneDeep} from "lodash"
import axios from "axios";
import InfiniteScroll from "@/components/InfiniteScroll.vue";
import DateHelper from "date-helper-util";
import {hasPermission} from "@/helpers/permissions";
import PageTitle from "@/components/PageTitle.vue";
import Vue from "vue";
import {ExtendedVue} from "vue/types/vue";

export default {
    name: "FrontendFilters",
    components: {PageTitle, InfiniteScroll, FrontendFilterInput, OrderSearchFilter},

    props: {
        filterGroup: {required: true, type: Object as () => FrontendFilterGroup},
        presetIdentifier: {required: false, type: String},
    },

    async mounted(): Promise<void> {
        const promises: any = []

        if (this.presetIdentifier) {
            // Load filter preset. This needs to be done first to be able to know which filters and columns must be preloaded
            await axios.get(`/api/rest/frontend-filter-presets/${this.filterGroup.identifier}_${this.filterGroup.subIdentifier}/${this.presetIdentifier}`)
                .then((response: Record<string, any>) => {
                    this.frontendFilterPreset = response.data
                })
                .catch((e: Error) => {
                    console.error(e)
                    // @ts-ignore
                    this.$alert(`Fout bij het ophalen van de presets.`, 'warning')
                })
        }

        this.filterConfig.availableFilterIdentifiers = (this.frontendFilterPreset?.available_filters)
            ? this.frontendFilterPreset.available_filters
            : this.filterGroup.filters.map((filter: FrontendFilter) => filter.identifier)

        this.filterConfig.filtersExpanded = (this.filtersExpandedStateFromLocalCache !== undefined)
            ? this.filtersExpandedStateFromLocalCache as boolean
            : this.frontendFilterPreset?.filters_expanded as boolean

        // Als de pagina is geopend met hash-parameters dan is het een copy-paste geweest van een eerdere search die dus gerepliceerd moet worden.
        // Om geen gekke combinatie te krijgen tussen waardes uit de cache en waardes uit de hash negeren we de cache dan dus.
        this.setFilterValues((this.hasURLSearchParams)
            ? this.getURLHashParameters()
            : this.filterValuesFromLocalCache
        )
        this.resetColumns()

        if (this.visibleColumnIdentifiersFromLocalCache.length) {
            this.filterConfig.visibleColumnIdentifiers = this.visibleColumnIdentifiersFromLocalCache
        }

        // Fire preload calls for the available filters
        this.availableFilters.forEach((filter: FrontendFilter) => {
            if (typeof filter.getOptions === 'function') {
                promises.push(
                    filter.getOptions()
                        .then((options: FrontendFilterOption[]) => filter.options = options)
                        .catch(e => console.error(e))
                )
            } else if (Array.isArray(filter.getOptions)) {
                filter.options = filter.getOptions
            }
        })

        this.headerComponent = this.filterGroup.headerComponent ? this.filterGroup.headerComponent() : null;

        // Op dit punt moeten we wachten tot alle preloads (van de filters) klaar zijn. Daarna kunnen we pas de search afvuren
        await Promise.all(promises)
        this.isLoadingFilters = false

        window.addEventListener("hashchange", this.handleHashChange);
        window.addEventListener("resize", this.checkIsViewportSmall);

        // Stel de hash opnieuw in omdat deze mogelijk is overschreven door waardes uit de preset. Echter, als deze niet veranderd is vuur de search dan handmatig af omdat dan de hashchange niet triggert.
        if (this.setURLHashParameters(this.filterConfig.filterValues) || window.location.hash.length <= 1) {
            this.search().then()
        }

        // Vuur de preload van de kolommen simultaan af met de search omdat de search wrs toch langer duurt
        return this.initializeVisibleColumns().then() // Explicitly empty then() to suppress IDE warning for missing await statement
    },

    beforeDestroy() {
        this.cancelSearch()
        window.removeEventListener("hashchange", this.handleHashChange);
        window.removeEventListener("resize", this.checkIsViewportSmall);
    },

    data() {
        return {
            isLoadingFilters: true,
            isLoadingColumns: true,
            isSearching: false,
            isLastPage: true,
            isColumnsContainerExpanded: false,
            isCancelled: false,
            showCancelButton: false,
            filterConfig: new FrontendFilterConfig(
                [],
                [],
                [],
                {},
                true,
            ),
            frontendFilterPreset: null as FrontendFilterPreset | null,
            results: [] as Record<string,any>[],
            axiosAbortController: new AbortController,
            lastSearchFilterValues: {},
            // @ts-ignore
            debounceFilterUpdate: _.debounce(this.debouncedFilterUpdate, 800),
            selectedResults: [] as Record<string,any>[],
            currentExecutionBatchActionIdentifier: null as string | null,
            batchActionModalComponent: null as object | null,
            headerComponent: null as object | null,
            isViewportSmall: false,
        }
    },

    computed: {
        cacheKey(): string {
            return `FrontendFilters_v1_${this.filterGroup.identifier}_${this.filterGroup.subIdentifier}_${this.presetIdentifier ?? 'noPreset'}`
        },
        availableFilters(): FrontendFilter[] {
            return _.chain([...this.filterConfig.availableFilterIdentifiers])
                .uniq()
                .map((identifier: string): (FrontendFilter | undefined) => this.getFilterFromIdentifier(identifier))
                .filter((filter: FrontendFilter | undefined): boolean => !!filter)
                .value() as FrontendFilter[]
        },
        availableColumns(): FrontendFilterResultColumn[] {
            return this.filterConfig.availableColumnIdentifiers
                .map((columnIdentifier: string) => this.getColumnFromIdentifier(columnIdentifier))
                .filter((column: FrontendFilterResultColumn | undefined) => column) as FrontendFilterResultColumn[]
        },
        visibleFilterFormFilters(): FrontendFilter[] {
            return this.availableFilters
                .filter((filter: FrontendFilter) => {
                    if (this.filterIdentifiersInVisibleColumns.includes(filter.identifier)) {
                        return false;
                    }

                    if (!filter.show) {
                        return true;
                    }

                    return filter.show();
                })
        },
        filterValues(): FrontendFilterValues {
            return this.filterConfig.filterValues
        },
        visibleColumnIdentifiers(): FrontendFilterValues {
            return this.filterConfig.visibleColumnIdentifiers
        },
        visibleColumns(): FrontendFilterResultColumn[] {
            return this.filterConfig.visibleColumnIdentifiers
                .map((identifier: string) => this.getColumnFromIdentifier(identifier))
                .filter(c => c) as FrontendFilterResultColumn[]
        },
        batchActionsToShow() {
            return this.filterGroup.batchActions
                .filter((batchAction: FrontendFilterBatchAction) => batchAction.show())
        },
        columnsWithFrontendFilterResultRowModifierCssClasses(): FrontendFilterResultColumn[]  {
            return this.filterConfig.availableColumnIdentifiers
                .map((identifier: string) => this.getColumnFromIdentifier(identifier))
                .filter((column: FrontendFilterResultColumn | undefined) => column?.resultRowModifier) as FrontendFilterResultColumn[]
        },
        filterIdentifiersInVisibleColumns(): string[] {
            return this.visibleColumns
                .filter((c: FrontendFilterResultColumn) => c.resultColumnFilterIdentifier)
                .map((c: FrontendFilterResultColumn) => c.resultColumnFilterIdentifier as string)
        },
        filterIdentifiersInAvailableColumns(): string[] {
            return this.availableColumns
                .map((column: FrontendFilterResultColumn) => column.resultColumnFilterIdentifier)
                .filter((filterIdentifier: string | undefined) => filterIdentifier) as string[]
        },
        presetValuesFilterIdentifiers(): string[] {
            return (this.frontendFilterPreset?.filter_values)
                ? Object.keys(this.frontendFilterPreset.filter_values)
                : []
        },
        lockedFilters(): string[] {
            return this.frontendFilterPreset?.locked_filters ?? []
        },
        hasURLSearchParams(): boolean {
            return Object.keys(this.getURLHashParameters()).length !== 0;
        },
        filtersExpandedState(): boolean {
            return this.filterConfig.filtersExpanded
        },
        filtersExpandedStateFromLocalCache(): boolean | undefined {
            try {
                return JSON.parse(localStorage.getItem(this.cacheKey) ?? '{}')?.filters_expanded ?? undefined
            } catch (e) {
                return undefined
            }
        },
        filterValuesFromLocalCache(): FrontendFilterValues {
            try {
                return JSON.parse(localStorage.getItem(this.cacheKey) ?? '{}')?.filters ?? {}
            } catch (e) {
                return {}
            }
        },
        visibleColumnIdentifiersFromLocalCache(): string[] {
            try {
                return JSON.parse(localStorage.getItem(this.cacheKey) ?? '{}')?.columns ?? []
            } catch (e) {
                return []
            }
        },
        nonEmptyFilterValues(): FrontendFilterValues {
            const nonEmptyFilterValues = {} as FrontendFilterValues
            _.each(this.filterConfig.filterValues, (value: any, filterIdentifier: string): void => {
                const filter: FrontendFilter | undefined = this.getFilterFromIdentifier(filterIdentifier)

                if (value === undefined || value === '' || !this.filterConfig.availableFilterIdentifiers.includes(filterIdentifier) || filter?.inputType.config.disabled?.(filter, this.filterConfig.filterValues)) {
                    return
                }

                let displayValue = this.getPillDisplayValue(filterIdentifier, value)
                if (!displayValue) {
                    return
                }

                nonEmptyFilterValues[filterIdentifier] = displayValue
            })

            return nonEmptyFilterValues
        },
        selectColumnsOptions(): Record<number,any> {
            return this.availableColumns
                .map((column: FrontendFilterResultColumn) => ({
                    text: this.getColumnTranslation(column.identifier, 'title'),
                    value: column.identifier,
                }))
        },
        availableBatchActions(): FrontendFilterBatchAction[] {
            return this.filterGroup.batchActions.filter((batchAction: FrontendFilterBatchAction) => batchAction.show())
        },
        resultRowModifiers(): FrontendFilterResultRowModifier[] {
            return this.filterGroup.resultRowModifiers
                    ?.map((resultRowModifierFunction: () => FrontendFilterResultRowModifier): FrontendFilterResultRowModifier => resultRowModifierFunction())
                ?? []
        },
        resultsWithParsedColumnValues(): Record<string,any>[] {
            return this.results
                .map((resultRow: Record<string,any>) => {
                    resultRow._columnValues = {}

                    this.availableColumns.forEach((column: FrontendFilterResultColumn) => {
                        resultRow._columnValues[column.identifier] = this.getColumnValue(column, resultRow)
                    })

                    return resultRow
                })
        },
    },

    methods: {
        debouncedFilterUpdate(): void {
            const Form = this.$refs['filterForm'] as HTMLFormElement | undefined

            if (!Form?.checkValidity()) {
                Form?.reportValidity()
                return
            }

            this.saveLocalCache();
            this.setURLHashParameters(this.filterConfig.filterValues);
            this.lastSearchFilterValues = structuredClone(this.filterConfig.filterValues)
        },
        async initializeVisibleColumns(): Promise<boolean> {
            const promises: any = []
            this.visibleColumns.forEach((column: FrontendFilterResultColumn) => {
                if (typeof column.preload === 'function') {
                    promises.push(column.preload())
                }
            })

            return Promise.all(promises)
                .then(() => this.isLoadingColumns = false)
        },
        async search(isNewSearch: boolean = true): Promise<void> {
            this.axiosAbortController.abort()
            this.isSearching = true
            this.showCancelButton = false

            if (isNewSearch) {
                this.results = []
            }

            const defaultParameters = (typeof this.filterGroup.searchApiDefaultParameters === 'function')
                ? this.filterGroup.searchApiDefaultParameters(this.filterValues)
                : this.filterGroup.searchApiDefaultParameters

            const queryParams = {
                ...defaultParameters,
                ...this.normalizeFiltersValuesForSearch(),
                offset: this.results.length,
                limit: this.filterGroup.resultsPerPage,
            }

            const apiEndpoint = (typeof this.filterGroup.searchApiPath === 'function')
                ? this.filterGroup.searchApiPath(this.$route)
                : this.filterGroup.searchApiPath;

            this.axiosAbortController = new AbortController;
            return axios.get(`${apiEndpoint}?${GenericHelpers.toQueryString(queryParams)}`, {signal: this.axiosAbortController.signal})
                .then((response: Record<string,any>) => {
                    this.isLastPage = response.data.length < this.filterGroup.resultsPerPage

                    const FrontendFilterResultRowPreprocessor: FrontendFilterResultRowPreprocessor = this.filterGroup.resultRowPreprocessor
                        ?? ((resultRow: Record<string,any>): Record<string,any> => resultRow);

                    this.results = this.results.concat(
                        response.data.map(FrontendFilterResultRowPreprocessor)
                    )
                    this.checkIsViewportSmall();
                    this.isSearching = false
                })
                .catch((error: Record<string,any>) => {
                    console.error(error)
                    if (error.response?.status) {
                        // @ts-ignore
                        this.$alert(`Er is iets mis gegaan bij het ophalen van de resultaten`, 'danger')
                        this.isSearching = false
                        this.isLastPage = true
                    }
                })
        },
        normalizeFiltersValuesForSearch(): FrontendFilterValues {
            let normalizedFilterValues: FrontendFilterValues = structuredClone(this.filterConfig.filterValues)
            let filter: FrontendFilter | undefined,
                filterValue: any

            _.each(this.filterConfig.availableFilterIdentifiers, (filterIdentifier: string) => {
                filter = this.getFilterFromIdentifier(filterIdentifier)
                if (!filter || filter.inputType.config.disabled?.(filter, this.filterConfig.filterValues)) {
                    return
                }

                normalizedFilterValues = filter.normalizeValue?.(normalizedFilterValues) ?? normalizedFilterValues
            })

            _.each(normalizedFilterValues, (normalizedValue: any, filterIdentifier: string) => {
                if (!normalizedValue || (typeof normalizedValue === 'string' && !normalizedValue?.trim())) {
                    delete normalizedFilterValues[filterIdentifier]
                }
            })

            return normalizedFilterValues
        },
        getFilterFromIdentifier(identifier: string): FrontendFilter | undefined {
            return this.filterGroup.filters.find((filter: FrontendFilter) => filter.identifier === identifier)
        },
        getColumnFromIdentifier(identifier: string): FrontendFilterResultColumn | undefined {
            return this.filterGroup.columns.find((column: FrontendFilterResultColumn) => column.identifier === identifier)
        },

        resetFilters() {
            this.setFilterValues(this.frontendFilterPreset?.filter_values ?? {})
        },

        setFilterValues(newValues: Record<string,any> = {}): void {
            newValues = cloneDeep(newValues)

            this.filterConfig.filterValues = {}
            this.presetValuesFilterIdentifiers.forEach((filterIdentifier: string) => {
                if (!this.availableFilters.some((filter: FrontendFilter) => filter.identifier === filterIdentifier)) {
                    return
                }

                let value = cloneDeep(this.frontendFilterPreset?.filter_values[filterIdentifier])
                if (value === undefined) {
                    return
                }

                this.filterConfig.filterValues[filterIdentifier] = value
            })

            // Als een filter een default value heeft in `filter_values` maar niet voorkomt in `newValues` dan is deze bewust leegemaakt in de GUI
            // Dit moet mogelijk zijn. Voeg daarom deze key expliciet toe met een lege waarde zodat hij de default value later overschrijft
            this.presetValuesFilterIdentifiers.forEach((filterIdentifier: string) => {
                if (!newValues[filterIdentifier]) {
                    newValues[filterIdentifier] = undefined
                }
            })

            // Filter alle waardes uit newValues (de waardes die gezet willen worden) als ze in de locked filters voorkomen
            this.lockedFilters.forEach((filterIdentifier: string) => {
                delete newValues[filterIdentifier]
            })

            // Pak de default values en overschrijf deze met de (legale) nieuwe waardes
            this.filterConfig.filterValues = this.$set(this.filterConfig, 'filterValues', {
                ...this.filterConfig.filterValues,
                ...newValues,
            })
        },
        resetColumns(): void {
            this.filterConfig.availableColumnIdentifiers = (this.frontendFilterPreset?.available_columns)
                ? this.frontendFilterPreset.available_columns
                : this.filterGroup.columns.map((column: FrontendFilterResultColumn) => column.identifier)

            this.filterConfig.visibleColumnIdentifiers = (this.frontendFilterPreset?.visible_columns)
                ? this.frontendFilterPreset.visible_columns
                : this.filterGroup.columns.map((column: FrontendFilterResultColumn) => column.identifier)
        },

        /**
         * @return bool True if the hash has changed
         */
        setURLHashParameters(filterValues: FrontendFilterValues): boolean {
            const oldHash          = window.location.hash
            const searchFilter     = filterValues;
            const searchParams     = new URLSearchParams();
            const fieldsToAdd      = this.availableFilters.map((filter: FrontendFilter) => filter.identifier);

            for (const [filterIdentifier, filterValue] of Object.entries(searchFilter)) {
                if (!fieldsToAdd.includes(filterIdentifier)) {
                    continue; // Alleen opties toevoegen aan URL params die ook zichtbaar zijn in GUI
                }

                if (filterValue === '' || filterValue == null) {
                    continue; // Sla lege waarden over
                }

                if (Array.isArray(filterValue)) {
                    filterValue.forEach(item => searchParams.append(filterIdentifier, item));
                } else {
                    let normalizedValues: FrontendFilterValues = (this.getFilterFromIdentifier(filterIdentifier)?.normalizeValue?.(filterValues) ?? filterValues)
                    searchParams.set(filterIdentifier, normalizedValues[filterIdentifier]);
                }
            }

            window.location.hash = searchParams.toString();
            return `#${window.location.hash}` !== oldHash
        },

        getURLHashParameters(): Record<string,any> {
            const hash = window.location.hash.substring(1); // Verwijder '#' uit URL
            const searchParams = new URLSearchParams(hash);
            const params: Record<string,any> = {};

            let isArray, cleanKey
            for (const [key, value] of searchParams) {
                if (key.endsWith('[]') || (isArray = this.getFilterFromIdentifier(key)?.inputType?.valueType === Array)) {
                    cleanKey = (!isArray) ? key.slice(0, -2) : key;

                    if (this.frontendFilterPreset?.locked_filters?.includes(key)) {
                        continue
                    }

                    if (!params[cleanKey]) {
                        params[cleanKey] = [];
                    }
                    params[cleanKey].push(value);
                } else {
                    if (this.frontendFilterPreset?.locked_filters?.includes(key)) {
                        continue
                    }

                    params[key] = value;
                }
            }

            return params;
        },

        handleHashChange() {
            this.setFilterValues(this.getURLHashParameters());
            this.search()
        },

        saveLocalCache(): void {
            localStorage.setItem(this.cacheKey, JSON.stringify({
                filters_expanded: this.filterConfig.filtersExpanded,
                filters: this.filterConfig.filterValues,
                columns: this.filterConfig.visibleColumnIdentifiers,
            }));
        },

        getColumnValue(column: FrontendFilterResultColumn, result: Record<string,any>): string | object | undefined {
            try {
                return column.getValue(result);
            }
            catch (error: any) {
                console.error([error, column, result])
                return undefined
            }
        },

        isFilterValueEditable(filterIdentifier: string): boolean {
            return !this.lockedFilters.includes(filterIdentifier)
        },
        removeFilterValue(filterIdentifier: string): void {
            this.$delete(this.filterConfig.filterValues, filterIdentifier)
        },

        getResultRowModifierCssClasses(resultRow: Record<string,any>): Record<string,boolean> {
            const cssClasses: Record<string,boolean> = {}

            let modifierCssClass: FrontendFilterResultRowModifierCssClasses | undefined;
            this.resultRowModifiers.forEach((resultRowModifier: FrontendFilterResultRowModifier): void => {
                modifierCssClass = (resultRowModifier?.test(resultRow, this.filterValues)) ? resultRowModifier.cssClass : undefined
                if (!modifierCssClass) {
                    return
                }

                cssClasses[modifierCssClass] = true
            })

            return cssClasses
        },

        getResultRowModifierIcons(resultRow: Record<string,any>): Array<any[]> {
            const icons: Array<any[]> = []

            this.resultRowModifiers.forEach((resultRowModifier: FrontendFilterResultRowModifier): void => {
                if (!resultRowModifier?.icon || !resultRowModifier?.test(resultRow, this.filterValues)) {
                    return
                }

                icons.push([
                    resultRowModifier?.icon ?? '',
                    resultRowModifier?.iconCssClass,
                    resultRowModifier?.iconTooltipLanguageKey
                        ? this.$t(resultRowModifier?.iconTooltipLanguageKey)
                        : null,
                ]);
            })

            return icons
        },

        isResultRowSelectableForBatchAction(resultRow: Record<string,any>): boolean {
            return !this.resultRowModifiers.some((resultRowModifier: FrontendFilterResultRowModifier): boolean => {
                return !resultRowModifier.isSelectableForBatchActions
                    && resultRowModifier.test(resultRow, this.filterValues)
            })
        },

        getColumnTranslation(columnIdentifier: string, field: string): string {
            const translation = this.$t(`frontend-filters.${this.filterGroup.identifier}.columns.${columnIdentifier}`)

            return (typeof translation !== 'string')
                ? translation[field]?.toString()
                : this.$t(`frontend-filters.${this.filterGroup.identifier}.filters.${columnIdentifier}.${field}`) as string
        },

        getPillDisplayValue(filterIdentifier: string | number, filterValue: any): string {
            const filter: FrontendFilter | undefined = this.getFilterFromIdentifier(filterIdentifier.toString())
            return filter?.inputType.getDisplayValue(filterValue, filter?.options ?? []) as string
        },

        cancelSearch(): void {
            this.isCancelled = true
            this.axiosAbortController.abort()
            axios.get(`/api/rest/sales-orders-search/cancel`).then(() => {})
            this.results = []
            this.isSearching = false
            this.isLastPage = true
        },

        toggleSelection(): void {
            if (this.selectedResults.length > 0) {
                this.selectedResults = []
                return
            }

            this.selectedResults = []
            _.each(this.results, (order) => {
                this.selectedResults.push(order)
            })
        },

        executeBatchAction(batchAction: FrontendFilterBatchAction): void {
            this.currentExecutionBatchActionIdentifier = batchAction.identifier
            const executeOutput = batchAction.execute(this.selectedResults)

            if (executeOutput instanceof Promise) {
                executeOutput
                    .catch(e => {
                        console.error([e])
                        this.$alert('Er is iets misgegaan. Probeer het opnieuw.', 'danger')
                    })
                    .then(() => this.currentExecutionBatchActionIdentifier = null)
            }
            else {
                this.batchActionModalComponent = executeOutput
                this.$bvModal.show('frontend-filters-batch-action-modal')
            }
        },

        showFilterInColumnHeader(column: FrontendFilterResultColumn) {
            return column.resultColumnFilterIdentifier
                && this.filterConfig.availableFilterIdentifiers.includes(column.resultColumnFilterIdentifier)
                && this.getFilterFromIdentifier(column.resultColumnFilterIdentifier ?? '')
        },

        showColumnTooltip(column: FrontendFilterResultColumn): boolean {
            const filter: FrontendFilter | undefined = this.getFilterFromIdentifier(column.resultColumnFilterIdentifier ?? '')
            if (!filter) {
                return false
            }

            return (this.filterConfig.filterValues[column.resultColumnFilterIdentifier ?? ''] !== undefined
                && !this.getFilterFromIdentifier(column.resultColumnFilterIdentifier ?? '')?.inputType.config.disabled?.(filter, this.filterConfig.filterValues)
            ) as boolean
        },

        consoleLog(value: any): void {
            console.log(value)
        },
        checkIsViewportSmall(): boolean {
            return this.isViewportSmall = !!document.getElementById('viewportSmallTester')?.getBoundingClientRect()?.height
        }
    },

    watch: {
        filterValues: {
            deep: true,
            handler(newVal) {
                if (_.isEqual(newVal, this.lastSearchFilterValues)) {
                    return
                }
                this.debounceFilterUpdate()
            },
        },
        visibleColumnIdentifiers(newVal) {
            this.saveLocalCache()
        },
        initializeVisibleColumns() {
            this.isLoadingColumns = true
            this.initializeVisibleColumns()
        },
        filtersExpandedState() {
            this.saveLocalCache()
        },
    },
}
